import { ApolloError } from '@apollo/client/core'

import {
  type CheckScratchcardOrderQuery,
  type OrderTypeQuery,
  PlaySlipType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

export const ORDER_TYPES = {
  Keno: 'keno',
  Scratchcard: 'scratchcard',
  SingleOrder: 'single_order',
  Subscriptions: 'subscription',
} as const

export type OrderType = (typeof ORDER_TYPES)[keyof typeof ORDER_TYPES]

const ORDER_ROUTE_SEGMENT: Record<OrderType, string> = {
  [ORDER_TYPES.Keno]: 'keno',
  [ORDER_TYPES.Scratchcard]: 'scratchcard',
  [ORDER_TYPES.SingleOrder]: 'single-order',
  [ORDER_TYPES.Subscriptions]: 'subscription',
}

export const getOrderRoute = (orderType: OrderType, orderId: string) =>
  `/my-orders/${ORDER_ROUTE_SEGMENT[orderType]}/${orderId}`

const UNKNOWN_PLAY_SLIP_ORDER_ERROR = 'validation.playsliporder.unknown'

const isUnknownPlaySlipOrderError = (error: unknown) =>
  error instanceof ApolloError &&
  error.graphQLErrors.some(({ message }) => message === UNKNOWN_PLAY_SLIP_ORDER_ERROR)

export const useOrderType = () => {
  const {
    $apollo: { defaultClient },
  } = useNuxtApp()

  const getLotteryOrderType = async (orderId: string) => {
    try {
      const response = await defaultClient.query<OrderTypeQuery>({
        fetchPolicy: 'no-cache',
        query: OrderTypeDocument,
        variables: { orderId },
      })
      if (!response) return false

      return [PlaySlipType.FreeTicket, PlaySlipType.Normal].includes(response.data.orderType.type)
        ? ORDER_TYPES.SingleOrder
        : ORDER_TYPES.Subscriptions
    } catch (error) {
      // A keno order id is rejected with `validation.playsliporder.unknown`,
      // since singleOrderMultipleLines only handles lottery products
      if (isUnknownPlaySlipOrderError(error)) return ORDER_TYPES.Keno
      return false
    }
  }

  const isScratchcardOrderType = async (orderId: string) => {
    try {
      const response = await defaultClient.query<CheckScratchcardOrderQuery>({
        fetchPolicy: 'no-cache',
        query: ScratchcardOrderDocument,
        variables: { orderId },
      })

      return !!response
    } catch {
      return false
    }
  }

  const getOrderType = async (orderId: string) => {
    const lotteryOrderType = await getLotteryOrderType(orderId)
    if (
      lotteryOrderType === ORDER_TYPES.SingleOrder ||
      lotteryOrderType === ORDER_TYPES.Subscriptions
    ) {
      return lotteryOrderType
    }

    const isScratchcardOrder = await isScratchcardOrderType(orderId)
    if (isScratchcardOrder) return ORDER_TYPES.Scratchcard

    if (lotteryOrderType === ORDER_TYPES.Keno) return ORDER_TYPES.Keno

    return null
  }

  return { getOrderType }
}
